import type {
  MapTheme,
  PlayerId,
  TerrainOnlyTileKind,
  TileKind,
  UnitKind,
} from "@dailywars/core/engine";
import { isBuildingTile } from "@dailywars/core/engine";

import { UnitIconLayers } from "#/components/game/UnitIconLayers";
import { getRoadTileIconLayers } from "#/game/roadTileIcon";
import { getBuildingImageSrc, getTerrainImageSrc } from "#/game/svgAssets";
import {
  getSpriteTerrainImageSrc,
  usesSpriteTerrainFullTile,
  usesSpriteTerrainOverlay,
} from "#/game/terrainSprites";
import { cn } from "#/lib/utils";

export function CodexUnitIcon({
  kind,
  player = "p1",
  className,
}: {
  kind: UnitKind;
  player?: PlayerId;
  className?: string;
}) {
  return (
    <UnitIconLayers
      kind={kind}
      player={player}
      className={cn("mx-auto h-10 w-10 shrink-0", className)}
    />
  );
}

function CodexPlainWithOverlayLayers({
  plainSrc,
  overlaySrc,
  className,
}: {
  plainSrc: string;
  overlaySrc: string;
  className: string;
}) {
  return (
    <>
      <img src={plainSrc} alt="" className={cn("absolute inset-0", className)} aria-hidden />
      <img src={overlaySrc} alt="" className={cn("relative z-10", className)} aria-hidden />
    </>
  );
}

function CodexTerrainTileLayers({
  kind,
  theme,
  className,
}: {
  kind: TerrainOnlyTileKind;
  theme?: MapTheme;
  className: string;
}) {
  if (usesSpriteTerrainFullTile({ kind })) {
    return (
      <img
        src={getSpriteTerrainImageSrc({ theme, kind })}
        alt=""
        className={className}
        aria-hidden
      />
    );
  }

  if (usesSpriteTerrainOverlay({ theme, kind })) {
    return (
      <CodexPlainWithOverlayLayers
        plainSrc={getTerrainImageSrc({ kind: "plain", x: 0, y: 0, theme })}
        overlaySrc={getSpriteTerrainImageSrc({ theme, kind })}
        className={className}
      />
    );
  }

  if (kind === "road") {
    const { plainSrc, overlaySrc } = getRoadTileIconLayers({ theme });
    return (
      <CodexPlainWithOverlayLayers
        plainSrc={plainSrc}
        overlaySrc={overlaySrc}
        className={className}
      />
    );
  }

  return (
    <img
      src={getTerrainImageSrc({ kind, x: 0, y: 0, theme })}
      alt=""
      className={className}
      aria-hidden
    />
  );
}

export function CodexTileLayers({
  kind,
  imageClassName,
  player = "p1",
  theme,
}: {
  kind: TileKind;
  imageClassName?: string;
  player?: PlayerId;
  theme?: MapTheme;
}) {
  const className = cn("size-full", imageClassName);

  if (isBuildingTile(kind)) {
    return (
      <CodexPlainWithOverlayLayers
        plainSrc={getTerrainImageSrc({ kind: "plain", x: 0, y: 0, theme })}
        overlaySrc={getBuildingImageSrc(kind, player)}
        className={className}
      />
    );
  }

  return (
    <CodexTerrainTileLayers
      kind={kind as TerrainOnlyTileKind}
      theme={theme}
      className={className}
    />
  );
}

export function CodexTileIcon({ kind, theme }: { kind: TileKind; theme?: MapTheme }) {
  const player = kind === "cannon" ? "p2" : "p1";
  return (
    <div className="relative mx-auto h-10 w-10 shrink-0 overflow-hidden">
      <CodexTileLayers kind={kind} theme={theme} player={player} />
    </div>
  );
}
